// School self-signup, ported from design/clearedto-rent/Signup.dc.html (the
// canonical pixel source; copy verbatim). Four numbered step cards + a sticky
// live preview. The SERVER stays the one derivation (POST /derive-requirements
// returns recommended values, reasons, warnings, and the real Global rungs;
// the client renders and compares, never derives).
//
// Deliberate stubs, named (a rework accounts for what it removes):
// - Fleet-policy extraction (LEADGEN Part 1): the drop zone is primary per the
//   spec, but the parser is not built, so a dropped file shows the processing
//   state then falls back to "type instead". Same for the COI reader in step 4
//   and the logo upload in step 2 (logo lands with the Ship C dashboard).
// - The comp's "What this costs your renters" estimate box is NOT ported: its
//   premium table is hardcoded demo data, and a client-side price table is a
//   second derivation of price. It arrives with the real quote endpoint.
// - display_name derives from the legal name (comp behavior); the old separate
//   display-name and website inputs were removed with the comp.
// Guard: tests/ctr-signup-states.test.js.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api.js';
import Footer from '@/components/Footer.jsx';
import Wordmark from '@/components/Wordmark.jsx';
import DropZone from '@/components/DropZone.jsx';
import AirportTypeahead from '@/components/AirportTypeahead.jsx';
import tokens from '@/brand-tokens.json';

const ACCENTS = tokens.accents;
const money = (v) => `$${Number(v || 0).toLocaleString('en-US')}`;
const monogramOf = (name) => String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || 'YS';
const stripSuffix = (name) => String(name || '').replace(/,?\s+(LLC|Inc\.?|Corp\.?|Co\.?)$/i, '').trim();

const ROLES = ['Owner', 'Chief Instructor', 'Manager', 'Club Officer', 'Front desk / dispatch', 'Other'];
const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut',
  DE: 'Delaware', DC: 'the District of Columbia', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho',
  IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine',
  MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico',
  NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee',
  TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia',
  WI: 'Wisconsin', WY: 'Wyoming',
};
const STATES = Object.keys(STATE_NAMES);
const FLAGS = [['tailwheel', 'Tailwheel'], ['retract', 'Retract'], ['multi', 'Multi'], ['seaplane', 'Seaplane'], ['helicopter', 'Helicopter']];

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

const luminance = (hex) => {
  const n = parseInt(String(hex).replace('#', ''), 16);
  return (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255;
};

function StepCard({ n, title, sub, children }) {
  return (
    <div className="rounded-panel border border-line bg-white p-[28px_30px]">
      <h3 className="flex items-center gap-2.5 text-base font-bold">
        <span className="inline-flex h-[26px] w-[26px] flex-none items-center justify-center rounded-pill bg-primary text-[12.5px] text-white">{n}</span>
        {title}
      </h3>
      {sub ? <div className="mb-[18px] mt-1.5 text-[13px] leading-[1.5] text-ink-dim">{sub}</div> : null}
      {children}
    </div>
  );
}

const FIELD_LABEL = 'mb-1.5 block text-xs font-bold uppercase tracking-[0.04em] text-ink-soft';
const FIELD_INPUT = 'w-full rounded-[9px] border-[1.5px] border-line p-[12px_14px] text-sm outline-none focus:border-accent';

export default function Signup() {
  // step 1
  const [slug, setSlug] = useState('');
  const [slugState, setSlugState] = useState(null); // {available, problem, suggestions}
  const [legalName, setLegalName] = useState('');
  const [homeAirport, setHomeAirport] = useState(null); // {code, name, city, state, closed} or {code, freeText}
  const [homeState, setHomeState] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactRole, setContactRole] = useState(ROLES[0]);
  const [email, setEmail] = useState('');
  const [licensedStates, setLicensedStates] = useState(null); // null = unknown, never a gate
  const [onList, setOnList] = useState(false);
  // step 2
  const [accent, setAccent] = useState(ACCENTS[0]);
  const [hexInput, setHexInput] = useState('');
  const [logoNote, setLogoNote] = useState(false);
  // step 3
  const [mode, setMode] = useState('upload'); // upload | fleet | shortcut
  const [fleetUpload, setFleetUpload] = useState('idle'); // idle | processing | fallback | skipped
  const [fleet, setFleet] = useState([]);
  const [newN, setNewN] = useState(''); const [adding, setAdding] = useState(false);
  const [shortHull, setShortHull] = useState('');
  const [shortDed, setShortDed] = useState('');
  const [derived, setDerived] = useState(null);
  const [chosen, setChosen] = useState(null); // overrides; null = accept recommendations
  const [liabConfirm, setLiabConfirm] = useState(false);
  const [dmgInput, setDmgInput] = useState('');
  const [offersCfi, setOffersCfi] = useState(true);
  // step 4
  const [coiState, setCoiState] = useState('idle'); // idle | processing | fallback
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  // ?slug= prefill (the unclaimed page's claim CTA arrives with it).
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('slug');
    if (q) setSlug(q.toLowerCase().replace(/[^a-z0-9-]/g, ''));
  }, []);

  useEffect(() => {
    api.get('/api/ctr/config').then((r) => setLicensedStates(r.licensed_states ?? null)).catch(() => {});
  }, []);

  const effectiveFleet = useMemo(() => (
    mode === 'shortcut'
      ? [{ n_number: 'FLEET', hull_value: shortHull === '' ? null : Number(String(shortHull).replace(/[^0-9]/g, '')), in_motion_deductible: shortDed === '' ? null : Number(String(shortDed).replace(/[^0-9]/g, '')), rentable: true, flags: {} }]
      : fleet
  ), [mode, fleet, shortHull, shortDed]);

  const debouncedSlug = useDebounced(slug, 400);
  useEffect(() => {
    if (!debouncedSlug) { setSlugState(null); return; }
    api.get(`/api/ctr/schools/slug-check?slug=${encodeURIComponent(debouncedSlug)}`)
      .then(setSlugState).catch(() => setSlugState(null));
  }, [debouncedSlug]);

  const fleetKey = useDebounced(JSON.stringify(effectiveFleet), 500);
  useEffect(() => {
    api.post('/api/ctr/schools/derive-requirements', { fleet: JSON.parse(fleetKey) })
      .then((r) => { setDerived(r); setChosen(null); setDmgInput(''); setLiabConfirm(false); })
      .catch(() => {});
  }, [fleetKey]);

  const requirements = useMemo(() => {
    if (!derived) return null;
    const rec = derived.recommended;
    return chosen || {
      liability: { limit: rec.liability.limit, pax_sublimit: rec.liability.pax_sublimit, excluding_passengers: false },
      damage_limit: { limit: rec.damage_limit.limit },
      deductible_reimb: { limit: rec.deductible_reimb.limit },
      medical: { limit: rec.medical.limit },
    };
  }, [derived, chosen]);

  const setLine = useCallback((line, value) => {
    setChosen((prev) => ({ ...(prev || requirements), [line]: value }));
  }, [requirements]);

  async function addAircraft() {
    const n = newN.trim().toUpperCase();
    if (!n) return;
    setAdding(true);
    let row = { n_number: n, make: null, model: null, year: null, hull_value: null, in_motion_deductible: null, rentable: true, flags: {}, input_method: 'manual' };
    try {
      const r = await api.get(`/api/ctr/faa/${encodeURIComponent(n)}`);
      row = {
        ...row,
        n_number: r.aircraft.n_number || n,
        make: r.aircraft.make, model: r.aircraft.model, year: r.aircraft.year,
        flags: r.aircraft.flags || {},
        faa_snapshot: r.aircraft.faa_snapshot,
        input_method: 'faa',
      };
    } catch { /* registry miss or outage: manual entry, marked as such */ }
    setFleet((f) => [...f, row]);
    setNewN('');
    setAdding(false);
  }

  async function submit() {
    setSubmitting(true); setSubmitError(null);
    try {
      const r = await api.post('/api/ctr/schools/signup', {
        slug, legal_name: legalName, display_name: stripSuffix(legalName) || null,
        home_airport: homeAirport?.code || null,
        home_airport_name: (!homeAirport?.freeText && homeAirport?.name)
          ? `${homeAirport.name}${homeAirport.closed ? ' (closed)' : ''}` : null,
        home_state: homeState || null, website: null,
        accent_hex: accent, contact_name: contactName, contact_role: contactRole,
        email, offers_cfi: offersCfi,
        fleet: effectiveFleet.filter((a) => a.n_number || a.hull_value),
        chosen_requirements: chosen, consent: true,
      });
      setResult(r);
      window.scrollTo(0, 0);
    } catch (e) {
      setSubmitError(e.body?.error || e.message);
      if (e.body?.suggestions) setSlugState({ available: false, suggestions: e.body.suggestions });
    } finally { setSubmitting(false); }
  }

  async function joinWaitlist() {
    try {
      await api.post('/api/ctr/waitlist', { kind: 'school', email, state: homeState, school_slug: slug || null, school_name: legalName || null });
      setOnList(true);
    } catch (e) { setSubmitError(e.body?.code === 'bad_email' ? 'Enter your work email above first, then join the list.' : e.message); }
  }

  const canSubmit = consent && slug && legalName && email && slugState?.available !== false && !submitting;
  const displayName = stripSuffix(legalName) || 'Your school';
  const monogram = monogramOf(displayName);
  const isFreemail = /@(gmail|yahoo|outlook|hotmail|aol|icloud|me|proton(mail)?(\.me)?|msn|live|comcast|att|verizon|ymail)\./i.test(email);
  const lowContrast = /^#[0-9a-fA-F]{6}$/.test(accent) && luminance(accent) > 0.55;
  const buttonColor = lowContrast ? tokens.colors.accent : accent;
  const gated = Array.isArray(licensedStates) && homeState && !licensedStates.includes(homeState);
  const rentableRows = effectiveFleet.filter((r) => r.rentable !== false && (r.hull_value || r.n_number));
  const showRec = derived && (mode === 'shortcut' || rentableRows.length > 0);
  const rec = derived?.recommended;
  const rungs = derived?.rungs;
  const maxDed = derived?.derived_from?.max_ded || 0;

  // Damage input snaps to a real rung (the rungs are the server's; the snap is
  // presentation, the chosen value is validated server-side at signup).
  const dmgTyped = Number(String(dmgInput).replace(/[^0-9]/g, '')) || 0;
  const dmgRungs = rungs?.damage || [];
  const topDmg = dmgRungs[dmgRungs.length - 1] || 0;
  const dmgCapped = dmgTyped > topDmg && topDmg > 0;
  const dmgSnapped = dmgTyped > 0 ? (dmgRungs.find((r) => r >= dmgTyped) ?? topDmg) : null;

  useEffect(() => {
    if (dmgSnapped !== null && requirements && dmgSnapped !== requirements.damage_limit.limit) {
      setLine('damage_limit', { limit: dmgSnapped });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dmgSnapped]);

  // One preview card, rendered twice: the desktop sticky aside and the
  // mobile collapsible below the form (spec Part 2). One markup, no drift.
  const previewCard = (
    <div className="overflow-hidden rounded-[24px] border border-line bg-white shadow-[0_12px_40px_rgba(15,23,42,0.10)]">
      <div className="flex items-center gap-[11px] p-[18px_20px_16px] text-white transition-colors" style={{ background: accent }}>
        <div className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-[9px] bg-white text-sm font-bold" style={{ color: accent }}>{monogram}</div>
        <div className="min-w-0">
          <div className="truncate text-[14.5px] font-bold">{displayName}</div>
          <div className="truncate text-[11px] opacity-75">{slug || 'yourschool'}.clearedto.rent</div>
        </div>
      </div>
      <div className="p-[18px_20px_6px]">
        <h4 className="mb-1 text-[15px] font-bold">You&apos;re 2 minutes from being <Wordmark className="text-primary" /></h4>
        <p className="mb-3.5 text-xs leading-[1.5] text-ink-dim">{displayName} requires every renter to carry the coverage below. We&apos;ve set it up for you.</p>
        <div className="overflow-hidden rounded-card border-[1.5px] text-[11.5px]" style={{ borderColor: accent }}>
          <div className="bg-teal-card p-[8px_11px] font-bold" style={{ color: accent }}>✓ {displayName}&apos;s required coverage</div>
          <div className="flex justify-between border-t border-fill p-[7px_11px]"><span className="text-ink-dim">Liability</span><b>{requirements ? `${money(requirements.liability.limit)}${requirements.liability.excluding_passengers ? ' excl. pax' : ` / ${money(requirements.liability.pax_sublimit)}`}` : '…'}</b></div>
          <div className="flex justify-between border-t border-fill p-[7px_11px]"><span className="text-ink-dim">Rented aircraft damage</span><b>{requirements ? money(requirements.damage_limit.limit) : '…'}</b></div>
          <div className="flex justify-between border-t border-fill p-[7px_11px]"><span className="text-ink-dim">Deductible reimbursement</span><b>{requirements ? (requirements.deductible_reimb.limit === 0 ? 'Not required' : money(requirements.deductible_reimb.limit)) : '…'}</b></div>
          <div className="flex justify-between border-t border-fill p-[7px_11px]"><span className="text-ink-dim">Medical expense</span><b>{requirements ? money(requirements.medical.limit) : '…'}</b></div>
        </div>
        <div className="mt-[13px] rounded-[9px] p-3 text-center text-[13px] font-bold text-white transition-colors" style={{ background: buttonColor }}>Get my quote →</div>
      </div>
      <div className="p-[12px_20px_18px] text-[9.5px] leading-[1.55] text-ink-faint">
        Coverage by Global Aerospace · Arranged by NDEMNIFLY Aviation Insurance · Your certificate is sent to {displayName} automatically.
      </div>
    </div>
  );

  // ── Success view ──────────────────────────────────────────────────────────
  if (result) {
    const sub = result.school.slug;
    const portal = `${sub}.clearedto.rent`;
    return (
      <div className="min-h-screen bg-paper px-6 pb-20 pt-9">
        <div className="mb-[30px] text-center">
          <a href="/" className="mb-3.5 inline-block text-xl text-primary"><Wordmark /></a>
        </div>
        <div className="mx-auto max-w-[760px] rounded-[18px] border border-line bg-white p-[52px_48px] text-center shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
          <div className="mx-auto mb-[18px] flex h-[58px] w-[58px] items-center justify-center rounded-pill bg-ok-bg text-[26px] text-ok-fg">✓</div>
          <h2 className="mb-2 text-[28px] font-bold tracking-[-0.02em]">{portal} is live</h2>
          <p className="mx-auto mb-[30px] max-w-[460px] text-[15px] leading-[1.6] text-ink-dim">
            Your portal, dashboard, poster and QR exist right now. Put the link where renters already look and the roster fills itself.
          </p>
          <div className="mb-7 grid gap-3.5 text-left sm:grid-cols-3">
            <div className="rounded-card-lg border border-line p-[18px]">
              <div className="mb-3 flex h-14 w-11 items-center justify-center rounded border border-line bg-fill text-[9px] font-bold text-ink-faint">11×17</div>
              <div className="text-[13.5px] font-bold">Lobby poster</div>
              <div className="mb-2.5 mt-[3px] text-xs text-ink-dim">Your logo, your QR, front-desk ready.</div>
              <a href={`/api/ctr/schools/${sub}/poster.pdf`} target="_blank" rel="noreferrer" className="text-[12.5px] font-semibold">Download PDF ↓</a>
            </div>
            <div className="rounded-card-lg border border-line p-[18px]">
              <div className="mb-3 mt-[22px] flex h-[34px] w-11 items-center justify-center rounded border border-line bg-fill text-[9px] font-bold text-ink-faint">8.5×11</div>
              <div className="text-[13.5px] font-bold">Counter sign</div>
              <div className="mb-2.5 mt-[3px] text-xs text-ink-dim">Sized for an acrylic holder.</div>
              <a href={`/api/ctr/schools/${sub}/poster.pdf?piece=counter_8x11`} target="_blank" rel="noreferrer" className="text-[12.5px] font-semibold">Download PDF ↓</a>
            </div>
            <div className="rounded-card-lg border border-line p-[18px]">
              <div className="mb-3 mt-1.5 h-11 w-11 rounded-md bg-ink/85" style={{ background: 'repeating-linear-gradient(90deg, currentColor 0 4px, transparent 4px 8px), repeating-linear-gradient(0deg, currentColor 0 4px, transparent 4px 8px)', color: tokens.colors.ink, opacity: 0.85 }} />
              <div className="text-[13.5px] font-bold">QR sticker</div>
              <div className="mb-2.5 mt-[3px] text-xs text-ink-dim">2-inch placard, dispatch-clipboard ready.</div>
              <a href={`/api/ctr/schools/${sub}/poster.pdf?piece=sticker_2in`} target="_blank" rel="noreferrer" className="text-[12.5px] font-semibold">Download PDF ↓</a>
            </div>
          </div>
          {result.school.verification === 'manual_pending' ? (
            <div className="mx-auto mb-6 max-w-[520px] rounded-card bg-outside-bg p-[13px_16px] text-left text-[12.5px] leading-[1.55] text-outside-fg">
              <b>Your dashboard carries a &quot;Verifying&quot; watermark for now.</b> It clears on its own the first time
              a policy binds naming {legalName || 'your school'}. Nothing for you to do, and renters can buy immediately.
            </div>
          ) : null}
          <a href={`https://${portal}/dashboard`} className="inline-block rounded-card bg-primary px-[30px] py-[15px] text-[15px] font-semibold text-white hover:bg-primary-deep">
            Open my dashboard →
          </a>
          <div className="mt-3.5 text-[12.5px] text-ink-faint">You can edit your fleet, requirements and branding any time.</div>
        </div>
        <Footer />
      </div>
    );
  }

  // ── Form view ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-paper px-4 pb-20 pt-9 lg:px-12">
      <div className="mb-[30px] text-center">
        <a href="/" className="mb-3.5 inline-block text-xl text-primary"><Wordmark /></a>
        <h1 className="text-[31px] font-bold tracking-[-0.02em]">Set up your portal</h1>
        <p className="mt-2 text-[15px] text-ink-dim">About ten minutes. Free, no contract. Your first renter can be covered today.</p>
      </div>

      <div className="mx-auto grid max-w-[1100px] items-start gap-6 lg:grid-cols-[1.35fr_1fr]">
        <div className="flex min-w-0 flex-col gap-4">

          <StepCard n="1" title="Claim your address" sub="This is the link you'll put on posters, in your enrollment packet, and on the dispatch counter.">
            <div className="mb-4">
              <label className={FIELD_LABEL}>Your portal address</label>
              <div className={`flex items-stretch overflow-hidden rounded-[9px] border-[1.5px] bg-white ${slugState?.available === false ? 'border-amber' : 'border-line'}`}>
                <input
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="yourschool"
                  className="min-w-0 flex-1 border-none p-[12px_14px] text-right text-sm text-ink outline-none"
                />
                <span className="flex-none border-l border-line bg-fill p-[12px_14px] text-sm font-semibold text-ink-dim">.clearedto.rent</span>
              </div>
              <div className="mt-[7px] min-h-[17px] text-[12.5px] font-semibold">
                {slugState && slug ? (
                  slugState.problem ? <span className="text-bad-fg">{slugState.problem}</span>
                    : slugState.available ? <span className="text-ok-fg">✓ {slug}.clearedto.rent is available</span> : null
                ) : null}
              </div>
              {slugState?.available === false && !slugState.problem ? (
                <div className="mt-2 rounded-[9px] bg-warn-bg p-[11px_13px] text-[12.5px] leading-[1.5] text-warn-fg">
                  <b>{slug}.clearedto.rent is taken.</b> Nearby addresses that are open:
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(slugState.suggestions || []).map((s) => (
                      <button key={s} type="button" onClick={() => setSlug(s)}
                        className="rounded-pill border border-amber bg-white px-3 py-[5px] text-xs font-semibold text-warn-deep hover:bg-warn-faint">
                        {s}.clearedto.rent
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="mb-3.5 grid items-start gap-3.5 sm:grid-cols-2">
              <div>
                <label className={FIELD_LABEL}>Legal name <span className="normal-case font-normal tracking-normal text-ink-faint">(on certificates)</span></label>
                <input className={FIELD_INPUT} value={legalName} onChange={(e) => setLegalName(e.target.value)} />
              </div>
              <div>
                <label className={FIELD_LABEL}>Home airport</label>
                <div className="flex gap-2">
                  <AirportTypeahead
                    code={homeAirport?.code || ''}
                    inputClass={`${FIELD_INPUT} uppercase`}
                    onPick={(a) => {
                      setHomeAirport(a);
                      if (!a.freeText && a.state && !homeState) setHomeState(a.state);
                    }}
                  />
                  <select value={homeState} onChange={(e) => { setHomeState(e.target.value); setOnList(false); }}
                    aria-label="State"
                    className="w-24 flex-none rounded-[9px] border-[1.5px] border-line bg-white p-[11px_8px] text-sm text-ink outline-none focus:border-accent">
                    <option value="">State</option>
                    {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                {homeAirport && !homeAirport.freeText && homeAirport.name ? (
                  <div className="mt-1.5 text-[11.5px] text-ink-faint">
                    {homeAirport.code} · {homeAirport.name}{homeAirport.closed ? ' (closed)' : ''}{homeAirport.city ? ` · ${[homeAirport.city, homeAirport.state].filter(Boolean).join(', ')}` : ''}
                  </div>
                ) : null}
                {gated ? (
                  <div className="mt-2 rounded-[9px] bg-warn-bg p-[11px_13px] text-[12.5px] leading-[1.55] text-warn-deep">
                    <b>We&apos;ve not landed in {STATE_NAMES[homeState]} yet.</b> Set up anyway: your roster, outside-COI
                    tracking and print kit all work today, and portal quoting switches on the moment we&apos;re licensed
                    there. Requests are exactly how we pick the next state.
                    {onList
                      ? <div className="mt-[9px] font-bold">✓ You&apos;re on the list, and your request is counted.</div>
                      : (
                        <button type="button" onClick={joinWaitlist}
                          className="mt-[9px] block rounded-lg border border-amber bg-white px-[13px] py-[7px] text-xs font-bold text-warn-deep hover:bg-warn-faint">
                          Put my school on the announcement list
                        </button>
                      )}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="grid gap-3.5 sm:grid-cols-2">
              <div>
                <label className={FIELD_LABEL}>Your name</label>
                <input className={FIELD_INPUT} value={contactName} onChange={(e) => setContactName(e.target.value)} />
              </div>
              <div>
                <label className={FIELD_LABEL}>Your role</label>
                <select value={contactRole} onChange={(e) => setContactRole(e.target.value)}
                  className="w-full rounded-[9px] border-[1.5px] border-line bg-white p-[11px_10px] text-sm text-ink outline-none focus:border-accent">
                  {ROLES.map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-3.5">
              <label className={FIELD_LABEL}>Work email</label>
              <input type="email" className={FIELD_INPUT} value={email} onChange={(e) => setEmail(e.target.value)} />
              {isFreemail ? (
                <div className="mt-2 rounded-[9px] bg-outside-bg p-[11px_13px] text-[12.5px] leading-[1.5] text-outside-fg">
                  <b>A personal address works fine.</b> Your portal goes live immediately; your dashboard just shows
                  a &quot;Verifying&quot; watermark until the first policy binds naming {legalName || 'your school'}. It clears itself.
                </div>
              ) : null}
            </div>
          </StepCard>

          <StepCard n="2" title="Make it look like yours" sub="Renters trust the page faster when it carries your logo. You can change this any time.">
            <div className="grid gap-3.5 sm:grid-cols-2">
              <div>
                <label className={FIELD_LABEL}>Logo</label>
                <DropZone
                  title="Drop a PNG or SVG here"
                  sublabel="or we'll use your initials until you do"
                  accept="image/png,image/svg+xml"
                  onFile={() => setLogoNote(true)}
                />
                {logoNote ? <div className="mt-2 text-[11.5px] text-ink-faint">Logo upload arrives with your dashboard; your monogram carries the portal until then.</div> : null}
              </div>
              <div>
                <label className={FIELD_LABEL}>Accent color</label>
                <div className="mt-1 flex gap-2">
                  {ACCENTS.map((c) => (
                    <button key={c} type="button" onClick={() => { setAccent(c); setHexInput(''); }}
                      className={`h-8 w-8 rounded-[9px] border-2 ${accent === c ? 'border-ink' : 'border-transparent'}`}
                      style={{ background: c }} aria-label={c} />
                  ))}
                </div>
                <div className="mt-2.5 flex items-center gap-2">
                  <input
                    value={hexInput}
                    onChange={(e) => {
                      const v = e.target.value.trim();
                      setHexInput(e.target.value);
                      if (/^#?[0-9a-fA-F]{6}$/.test(v)) setAccent(v.startsWith('#') ? v : `#${v}`);
                    }}
                    placeholder="#1a6b8a"
                    className="w-[110px] rounded-lg border-[1.5px] border-line p-[9px_11px] font-mono text-[13px] outline-none focus:border-accent"
                  />
                  <span className="text-[11.5px] text-ink-faint">or paste your brand&apos;s hex code</span>
                </div>
                {lowContrast ? (
                  <div className="mt-2 text-[11.5px] leading-[1.5] text-ink-faint">Light color: your header keeps it, buttons get a darker shade so they stay readable.</div>
                ) : null}
              </div>
            </div>
          </StepCard>

          <StepCard n="3" title="Tell us about your fleet" sub="You shouldn't have to know insurance to set a correct minimum. You know your airplanes, and your fleet policy knows the numbers.">
            {mode === 'upload' && fleetUpload === 'idle' ? (
              <>
                <DropZone
                  primary
                  title="Fastest way: drop your fleet policy or dec page"
                  sublabel="We read the hull values and deductibles for you and set your renter requirements from your real coverage. You'll also get a one-page summary of how your fleet coverage and renter requirements line up."
                  accept="application/pdf,image/*"
                  capture="environment"
                  onFile={() => {
                    setFleetUpload('processing');
                    setTimeout(() => { setMode('fleet'); setFleetUpload('fallback'); }, 1600);
                  }}
                />
                <button type="button" onClick={() => { setMode('fleet'); setFleetUpload('skipped'); }}
                  className="pt-2 text-[12.5px] font-semibold text-accent hover:text-primary">
                  Type your fleet instead
                </button>
              </>
            ) : null}

            {fleetUpload === 'processing' ? (
              <DropZone processing processingLabel="Reading your policy…"
                processingSub="Pulling each scheduled aircraft, hull values, deductibles, liability limits, and the expiration date." />
            ) : null}

            {mode === 'fleet' && fleetUpload !== 'processing' ? (
              <>
                {fleetUpload === 'fallback' ? (
                  <div className="mb-3 rounded-card bg-warn-bg p-[11px_14px] text-[12.5px] leading-[1.5] text-warn-fg">
                    We couldn&apos;t read this one automatically yet. Type your fleet below and we&apos;ll take it from here.
                  </div>
                ) : null}
                <div className="mb-3 overflow-hidden rounded-[11px] border border-line">
                  <div className="hidden bg-fill text-[10.5px] font-bold uppercase tracking-[0.05em] text-ink-dim sm:grid sm:grid-cols-[0.9fr_1.4fr_1fr_1fr_0.9fr_34px]">
                    <div className="p-[9px_12px]">N-number</div><div className="p-[9px_12px]">Make / model</div>
                    <div className="p-[9px_12px]">Insured hull value</div><div className="p-[9px_12px]">In-motion deductible</div>
                    <div className="p-[9px_12px]">Rentable</div><div />
                  </div>
                  {fleet.map((r, i) => {
                    const upd = (patch) => setFleet((f) => f.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                    return (
                      <div key={`${r.n_number}-${i}`} className={`grid grid-cols-1 items-center gap-y-1 border-t border-fill p-2 text-[13px] sm:grid-cols-[0.9fr_1.4fr_1fr_1fr_0.9fr_34px] sm:gap-y-0 sm:p-0 ${r.rentable === false ? 'bg-paper' : 'bg-white'}`}>
                        <div className="p-[10px_12px] font-bold">{r.n_number}</div>
                        <div className="p-[10px_12px] text-ink-soft">
                          {[r.make, r.model].filter(Boolean).join(' ') || <span className="text-ink-faint">Unknown</span>}
                          <span className="mt-0.5 flex flex-wrap gap-1">
                            {FLAGS.map(([key, label]) => {
                              const on = !!(r.flags && r.flags[key]);
                              return (
                                <button key={key} type="button"
                                  onClick={() => upd({ flags: { ...(r.flags || {}), [key]: !on } })}
                                  className={`rounded-pill border px-1.5 text-[10px] ${on ? 'border-accent bg-teal-fill text-primary font-semibold' : 'border-line bg-white text-ink-faint'}`}>
                                  {label}
                                </button>
                              );
                            })}
                          </span>
                        </div>
                        <div className="p-[6px_8px]">
                          <input inputMode="numeric" placeholder="from your hull policy" value={r.hull_value ?? ''}
                            onChange={(e) => upd({ hull_value: e.target.value === '' ? null : Number(e.target.value.replace(/[^0-9]/g, '')) })}
                            className="w-full rounded-[7px] border border-line p-[7px_9px] text-[13px] outline-none focus:border-accent" />
                        </div>
                        <div className="p-[6px_8px]">
                          <input inputMode="numeric" placeholder="in-motion" value={r.in_motion_deductible ?? ''}
                            onChange={(e) => upd({ in_motion_deductible: e.target.value === '' ? null : Number(e.target.value.replace(/[^0-9]/g, '')) })}
                            className="w-full rounded-[7px] border border-line p-[7px_9px] text-[13px] outline-none focus:border-accent" />
                        </div>
                        <div className="p-[10px_12px]">
                          <button type="button" onClick={() => upd({ rentable: r.rentable === false })}
                            className={`rounded-pill border-[1.5px] px-[11px] py-1 text-[11px] font-bold ${r.rentable !== false ? 'border-accent bg-teal-fill text-primary' : 'border-line bg-white text-ink-faint'}`}>
                            {r.rentable !== false ? 'Rentable' : 'Staff only'}
                          </button>
                        </div>
                        <button type="button" aria-label="Remove aircraft" onClick={() => setFleet((f) => f.filter((_, j) => j !== i))}
                          className="text-[15px] text-line-strong hover:text-bad-fg">×</button>
                      </div>
                    );
                  })}
                  <div className="flex items-center gap-2.5 border-t border-fill p-[10px_12px]">
                    <input value={newN} onChange={(e) => setNewN(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAircraft(); } }}
                      placeholder="Add an N-number, e.g. N12345"
                      className="w-[200px] rounded-[7px] border border-line p-[8px_10px] text-[13px] outline-none focus:border-accent" />
                    <button type="button" onClick={addAircraft} disabled={adding}
                      className="rounded-lg border-[1.5px] border-line-strong bg-white px-3.5 py-[7px] text-[12.5px] font-semibold text-primary hover:border-primary disabled:opacity-60">
                      {adding ? 'Looking up…' : 'Add aircraft'}
                    </button>
                    <span className="text-[11.5px] text-ink-faint">We prefill make and model from the FAA registry.</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-[18px]">
                  {fleetUpload === 'skipped' || fleetUpload === 'fallback' ? (
                    <button type="button" onClick={() => { setMode('upload'); setFleetUpload('idle'); }}
                      className="text-[12.5px] font-semibold text-accent hover:text-primary">
                      ← Or drop your fleet policy and we&apos;ll fill this in
                    </button>
                  ) : null}
                  <button type="button" onClick={() => setMode('shortcut')} className="text-[12.5px] font-semibold text-accent hover:text-primary">
                    Don&apos;t want to type the fleet? Use the two-question shortcut
                  </button>
                </div>
              </>
            ) : null}

            {mode === 'shortcut' ? (
              <>
                <div className="mb-3 grid gap-3.5 sm:grid-cols-2">
                  <div>
                    <label className={FIELD_LABEL}>Highest hull value a renter can fly</label>
                    <input inputMode="numeric" className={FIELD_INPUT} value={shortHull} onChange={(e) => setShortHull(e.target.value)} placeholder="60,000" />
                  </div>
                  <div>
                    <label className={FIELD_LABEL}>Highest deductible in your fleet</label>
                    <input inputMode="numeric" className={FIELD_INPUT} value={shortDed} onChange={(e) => setShortDed(e.target.value)} placeholder="5,000" />
                  </div>
                </div>
                <button type="button" onClick={() => { setMode('fleet'); if (fleetUpload === 'idle') setFleetUpload('skipped'); }}
                  className="text-[12.5px] font-semibold text-accent hover:text-primary">
                  ← Back to the fleet table (better recommendations, and per-aircraft clearing later)
                </button>
              </>
            ) : null}

            {showRec && rec && requirements ? (
              <div className="mt-[18px] rounded-card-lg border-[1.5px] border-teal-line bg-teal-faint p-[18px_20px]">
                <div className="mb-1.5 flex items-center justify-between">
                  <div className="text-[13px] font-bold text-primary">Your recommended renter requirements</div>
                  <span className="rounded-pill border border-line bg-white px-[9px] py-1 text-[10px] font-bold uppercase tracking-[0.05em] text-ink-dim">derived from your fleet</span>
                </div>
                <div className="mb-3.5 text-xs leading-[1.5] text-ink-dim">
                  Every value is snapped to a limit Global actually sells. Change any of them; your changes are kept and marked as overrides.
                </div>

                <div className="border-b border-teal-fill py-3">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-[13.5px] text-ink-soft">Liability, each occurrence / per passenger</span>
                    <select
                      value={`${requirements.liability.limit}/${requirements.liability.excluding_passengers ? 'x' : requirements.liability.pax_sublimit}`}
                      onChange={(e) => {
                        const [lim, pax] = e.target.value.split('/');
                        setLiabConfirm(false);
                        setLine('liability', pax === 'x'
                          ? { limit: Number(lim), pax_sublimit: null, excluding_passengers: true }
                          : { limit: Number(lim), pax_sublimit: Number(pax), excluding_passengers: false });
                      }}
                      className="rounded-lg border-[1.5px] border-line bg-white p-[8px_10px] text-[13px] font-semibold text-ink outline-none">
                      {(rungs?.liability || []).map((r) => (
                        <option key={`${r.limit}/${r.excluding_passengers ? 'x' : r.pax_sublimit}`} value={`${r.limit}/${r.excluding_passengers ? 'x' : r.pax_sublimit}`}>
                          {money(r.limit)}{r.excluding_passengers ? ' excl. passengers' : ` / ${money(r.pax_sublimit)}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mt-[5px] text-[11.5px] text-teal-mute">{rec.liability.reason}</div>
                  {requirements.liability.limit < rec.liability.limit ? (
                    <div className="mt-2 rounded-[9px] bg-warn-bg p-[10px_12px] text-xs leading-[1.5] text-warn-fg">
                      This is below what most schools, and their own liability carriers, expect. You can keep it; we&apos;ll note it as your explicit choice.
                      <label className="mt-2 flex cursor-pointer items-center gap-2 font-semibold">
                        <input type="checkbox" checked={liabConfirm} onChange={(e) => setLiabConfirm(e.target.checked)} />
                        I understand, keep the lower minimum
                      </label>
                    </div>
                  ) : null}
                </div>

                <div className="border-b border-teal-fill py-3">
                  <div className="flex items-center justify-between gap-4">
                    <label htmlFor="ctr-dmg" className="text-[13.5px] text-ink-soft">Damage to rented aircraft</label>
                    <span className="flex items-center gap-2">
                      {dmgSnapped !== null && dmgSnapped !== rec.damage_limit.limit ? (
                        <span className="rounded-pill bg-warn-bg px-2 py-[3px] text-[9.5px] font-bold uppercase tracking-[0.05em] text-warn-fg">override</span>
                      ) : null}
                      <span className="flex items-center rounded-lg border-[1.5px] border-line bg-white pl-2.5">
                        <span className="text-[13px] font-semibold text-ink-faint">$</span>
                        <input id="ctr-dmg" inputMode="numeric" value={dmgInput} onChange={(e) => setDmgInput(e.target.value)}
                          placeholder={Number(rec.damage_limit.limit).toLocaleString('en-US')}
                          className="w-[86px] border-none bg-transparent p-[8px_10px_8px_4px] text-[13px] font-semibold text-ink outline-none" />
                      </span>
                    </span>
                  </div>
                  <div className="mt-[5px] text-[11.5px] text-teal-mute">{rec.damage_limit.reason}</div>
                  {dmgCapped ? (
                    <div className="mt-2 rounded-[9px] bg-warn-bg p-[10px_12px] text-xs leading-[1.5] text-warn-fg">
                      {money(topDmg)} is the top limit Global sells, so the requirement is capped there.
                    </div>
                  ) : null}
                </div>

                <div className="border-b border-teal-fill py-3">
                  <div className="flex items-center justify-between gap-4">
                    <label htmlFor="ctr-ded" className="text-[13.5px] text-ink-soft">Deductible reimbursement</label>
                    <span className="flex items-center gap-2">
                      {requirements.deductible_reimb.limit !== rec.deductible_reimb.limit ? (
                        <span className="rounded-pill bg-warn-bg px-2 py-[3px] text-[9.5px] font-bold uppercase tracking-[0.05em] text-warn-fg">override</span>
                      ) : null}
                      <select id="ctr-ded" value={String(requirements.deductible_reimb.limit)}
                        onChange={(e) => setLine('deductible_reimb', { limit: Number(e.target.value) })}
                        className="rounded-lg border-[1.5px] border-line bg-white p-[8px_10px] text-[13px] font-semibold text-ink outline-none">
                        <option value="0">Not required</option>
                        {(rungs?.deductibleReimb || []).map((r) => <option key={r} value={String(r)}>{money(r)}</option>)}
                      </select>
                    </span>
                  </div>
                  <div className="mt-[5px] text-[11.5px] text-teal-mute">{rec.deductible_reimb.reason}</div>
                  {requirements.deductible_reimb.limit === 0 && maxDed > 0 ? (
                    <div className="mt-2 rounded-[9px] bg-warn-bg p-[10px_12px] text-xs leading-[1.5] text-warn-fg">
                      Renters would pay every dollar of your {money(maxDed)} deductible out of pocket. Allowed, but most schools require reimbursement.
                    </div>
                  ) : requirements.deductible_reimb.limit > 0 && requirements.deductible_reimb.limit < maxDed ? (
                    <div className="mt-2 rounded-[9px] bg-warn-bg p-[10px_12px] text-xs leading-[1.5] text-warn-fg">
                      Won&apos;t fully cover your {money(maxDed)} deductible; a renter could still owe {money(maxDed - requirements.deductible_reimb.limit)} after a claim.
                    </div>
                  ) : requirements.deductible_reimb.limit > rec.deductible_reimb.limit ? (
                    <div className="mt-2 rounded-[9px] bg-warn-bg p-[10px_12px] text-xs leading-[1.5] text-warn-fg">
                      This is more than any deductible on your fleet; renters would pay for coverage they can&apos;t use.
                    </div>
                  ) : null}
                </div>

                <div className="py-3">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-[13.5px] text-ink-soft">Medical expense</span>
                    <span className="text-[13px] font-semibold">{money(rec.medical.limit)} <span className="text-[11px] font-normal text-ink-faint">included</span></span>
                  </div>
                  <div className="mt-[5px] text-[11.5px] text-teal-mute">{rec.medical.reason}</div>
                </div>

                {(derived.warnings || []).map((w) => (
                  <div key={w} className="mt-2 rounded-[9px] bg-warn-bg p-[10px_12px] text-xs leading-[1.5] text-warn-fg">{w}</div>
                ))}
              </div>
            ) : null}

            <label className="mt-3.5 flex cursor-pointer items-center gap-2 text-[13px] text-ink-soft">
              <input type="checkbox" checked={offersCfi} onChange={(e) => setOffersCfi(e.target.checked)} />
              We also use instructors (CFIs can be covered through the same portal)
            </label>
            <div className="pt-3.5 text-xs leading-[1.55] text-ink-faint">
              Your CFIs use the same portal. When a pilot with a flight instructor certificate buys, they&apos;re offered
              CFI non-owned professional liability for themselves; it&apos;s their coverage, not something you supply.
            </div>
          </StepCard>

          <StepCard n="4" title="Bring your existing certificates" sub="Optional, but it makes your roster complete on day one. Drop the folder, or skip and add them later.">
            {coiState === 'idle' ? (
              <DropZone
                title="Drop PDFs or photos of the COIs you have on file"
                sublabel="We read the carrier, limits and expiration, grade each against your requirements, and load your roster"
                accept="application/pdf,image/*"
                capture="environment"
                onFile={() => { setCoiState('processing'); setTimeout(() => setCoiState('fallback'), 1600); }}
              />
            ) : null}
            {coiState === 'processing' ? (
              <DropZone processing processingLabel="Reading your certificates…"
                processingSub="Pulling carrier, limits, deductible reimbursement, and expiration from each one." />
            ) : null}
            {coiState === 'fallback' ? (
              <div className="rounded-card bg-warn-bg p-[11px_14px] text-[12.5px] leading-[1.5] text-warn-fg">
                COI reading arrives shortly. Your renters can log certificates from day one on your portal, and nothing
                else here waits on it.
              </div>
            ) : null}
            <label className="mt-[18px] flex cursor-pointer items-start gap-2.5 text-[12.5px] leading-[1.55] text-ink-soft">
              <input type="checkbox" className="mt-[3px]" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              <span>
                I&apos;m authorized to set this up for {legalName || 'my school'}. I understand the portal is free to my
                school, that coverage is arranged by NDEMNIFLY Aviation Insurance (CA Lic. #6018700) and underwritten
                by Global Aerospace, and that my school receives no payment for renters who buy insurance.
              </span>
            </label>
          </StepCard>

          <div className="flex flex-wrap items-center justify-between gap-4 rounded-panel bg-primary-deep p-[20px_24px] text-white">
            <div>
              <div className="text-[15px] font-bold">Everything&apos;s set. Go live?</div>
              <div className="mt-[3px] text-[12.5px] text-teal-mute-dark">Your dashboard, poster and QR are ready. You can edit your fleet, requirements and branding any time.</div>
              {submitError ? <div className="mt-1.5 text-[12.5px] font-semibold text-warn-line">{submitError}</div> : null}
            </div>
            <button type="button" onClick={submit} disabled={!canSubmit}
              className="flex-none rounded-card bg-white px-6 py-[13px] text-sm font-bold text-primary hover:bg-fill disabled:cursor-not-allowed disabled:opacity-60">
              {submitting ? 'Launching…' : `Launch ${slug || 'yourschool'}.clearedto.rent`}
            </button>
          </div>

          <details className="rounded-panel border border-line bg-white lg:hidden">
            <summary className="cursor-pointer list-none p-[16px_20px] text-[13.5px] font-bold text-primary">
              Preview your portal ▾
            </summary>
            <div className="p-[0_16px_16px]">{previewCard}</div>
          </details>
        </div>

        <div className="sticky top-6 hidden lg:block">
          <div className="mb-2.5 text-center text-[11px] font-bold uppercase tracking-[0.1em] text-ink-faint">Live preview · what your renters see</div>
          {previewCard}
          <div className="mt-3.5 flex items-center gap-3.5 rounded-card-lg border border-line bg-white p-[15px]">
            <div className="h-[58px] w-[58px] flex-none rounded opacity-85" style={{ background: `repeating-linear-gradient(90deg, ${tokens.colors.ink} 0 4px, transparent 4px 8px), repeating-linear-gradient(0deg, ${tokens.colors.ink} 0 4px, transparent 4px 8px)` }} />
            <div className="text-[12.5px] leading-[1.5] text-ink-soft">
              <b className="block text-[13px] text-ink">Your poster &amp; QR are ready the moment you launch</b>
              Printable front-desk sign, enrollment-packet insert, and a link for your website.
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
